// ---------------------------------------------------------------------------
// Stats Counter Renderer — Animated Statistics with IntersectionObserver
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, jsString } from "../escape.js";

interface StatItem {
  value: string;
  label: string;
  prefix?: string;
  suffix?: string;
}

interface StatsCounterProps {
  heading?: string;
  items: StatItem[];
  animate?: boolean;
  variant?: "bar" | "cards";
}

function renderStaticItem(item: StatItem, index: number): string {
  const prefix = item.prefix ? `<span class="ease-stat-prefix">${text(item.prefix)}</span>` : "";
  const suffix = item.suffix ? `<span class="ease-stat-suffix">${text(item.suffix)}</span>` : "";

  return `    <div class="ease-stat-item" data-stat-index="${attr(String(index))}" data-stat-value="${attr(item.value)}">
      <div class="ease-stat-value">${prefix}<span class="ease-stat-number">${text(item.value)}</span>${suffix}</div>
      <div class="ease-stat-label">${text(item.label)}</div>
    </div>`;
}

function buildScript(uid: string): string {
  return `<script>(function(){var el=document.querySelector('[data-stats-id="${jsString(uid)}"]');if(!el)return;var nums=el.querySelectorAll('.ease-stat-number');var dur=1500;var done=false;var obs=new IntersectionObserver(function(entries){entries.forEach(function(e){if(e.isIntersecting&&!done){done=true;obs.disconnect();nums.forEach(function(n){var raw=n.dataset.target||'0';var target=parseFloat(raw.replace(/[^0-9.]/g,''));if(isNaN(target)){n.textContent=raw;return;}var isFloat=raw.indexOf('.')!==-1;var start=0;var startTime=null;function step(ts){if(!startTime)startTime=ts;var p=Math.min((ts-startTime)/dur,1);var val=start+(target-start)*p;n.textContent=isFloat?val.toFixed(1):String(Math.floor(val));if(p<1)requestAnimationFrame(step);}requestAnimationFrame(step);});}});},{threshold:0.2});obs.observe(el);})()</script>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as StatsCounterProps;

  const items = Array.isArray(p.items) ? p.items : [];
  const animate = p.animate !== false;
  const variant = p.variant ?? "bar";

  const headingHtml = p.heading
    ? `<h2 class="ease-stats-heading">${text(p.heading)}</h2>\n  `
    : "";

  const containerStyle = variant === "cards"
    ? "display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:24px"
    : "display:flex;justify-content:center;gap:48px;flex-wrap:wrap";

  const uid = `sc-${attr(String(props.id ?? p.heading ?? p.items?.[0]?.label ?? "stats").replace(/[^a-z0-9]/gi, "").slice(0, 16))}`;

  const itemsHtml = items.map((item, i) => renderStaticItem(item, i)).join("\n");

  if (!animate) {
    return `<section class="ease-section ease-stats-counter ease-stats--${attr(variant)}" aria-label="${attr(p.heading ?? "Statistics")}">
  ${headingHtml}<div class="ease-stats-grid" style="${attr(containerStyle)}">
${itemsHtml}
  </div>
</section>`;
  }

  // For animated variant, add data attributes for JS targeting
  const animatedItemsHtml = items
    .map((item, i) => {
      const prefix = item.prefix ? `<span class="ease-stat-prefix">${text(item.prefix)}</span>` : "";
      const suffix = item.suffix ? `<span class="ease-stat-suffix">${text(item.suffix)}</span>` : "";

      return `    <div class="ease-stat-item" data-stat-index="${attr(String(i))}" data-stat-value="${attr(item.value)}">
      <div class="ease-stat-value">${prefix}<span class="ease-stat-number" data-target="${attr(item.value)}">0</span>${suffix}</div>
      <div class="ease-stat-label">${text(item.label)}</div>
    </div>`;
    })
    .join("\n");

  return `<section class="ease-section ease-stats-counter ease-stats--${attr(variant)}" aria-label="${attr(p.heading ?? "Statistics")}" data-stats-id="${attr(uid)}">
  ${headingHtml}<div class="ease-stats-grid" style="${attr(containerStyle)}">
${animatedItemsHtml}
  </div>
  <noscript><div class="ease-stats-grid" style="${attr(containerStyle)}">
${itemsHtml}
  </div></noscript>
</section>
${buildScript(uid)}`;
}

registerRenderer("stats-counter", { render });
